/* ============================================================
   Per-route SEO without a dependency.

   CAVEAT worth knowing: this runs client-side. Google executes JS and will
   see these tags, but most social scrapers (Facebook, LinkedIn, WhatsApp,
   Twitter) do NOT — they read the raw HTML. The og:/twitter: tags baked into
   public/index.html are the ones link previews actually use, so keep those
   accurate for the site as a whole. Proper per-route previews need
   prerendering or SSR; see REDESIGN.md.
   ============================================================ */
import { useEffect } from "react";

export const SITE_URL = "https://cafevistaai.com";
export const SITE_NAME = "CafeVista";

function setMeta(selector, attr, value) {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, key, val] = selector.match(/\[(\w+)="([^"]+)"\]/) || [];
    if (key && val) el.setAttribute(key, val);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/* Adds (and cleans up) a JSON-LD block for the current route. */
function setJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.id = id;
  el.textContent = JSON.stringify(data);
  document.head.appendChild(el);
}

/**
 * @param {object} o
 * @param {string} o.title       full <title> for the route
 * @param {string} o.description meta description, ~150-160 chars
 * @param {string} o.path        route path, e.g. "/service"
 * @param {object} [o.jsonLd]    optional structured data for this route
 */
export function useSeo({ title, description, path, jsonLd }) {
  useEffect(() => {
    const url = `${SITE_URL}${path || ""}`;
    if (title) document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setLink("canonical", url);

    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);

    setJsonLd("cv-route-jsonld", jsonLd);
    return () => setJsonLd("cv-route-jsonld", null);
  }, [title, description, path, jsonLd]);
}

/* ---------- copy for each route ----------
   Titles stay under ~60 chars, descriptions ~150-160, and each one leads with
   what the page actually offers rather than the company name. */
export const SEO = {
  home: {
    title: "CafeVista — Business Solutions, ERP Integration & Custom Systems",
    description:
      "We connect your ERP, databases and tools into one system with live reporting on top — SAP, Oracle, Dynamics, Salesforce, Odoo, NetSuite, or our own CollaBiz platform.",
    path: "/home",
  },
  about: {
    title: "About CafeVista | Business Solutions Company in Islamabad",
    description:
      "Founded 2018. We study how a business actually runs, engineer the system that fixes the bottleneck, and integrate it with the software the business already owns.",
    path: "/about",
  },
  solutions: {
    title: "Solutions | ERP Integration, Data Platforms & AI Agent Connect",
    description:
      "Four solution pillars: Data Sphere, ERP & enterprise systems, integration & automation, and custom business software. We build on the systems you already run.",
    path: "/service",
  },
  products: {
    title: "Products | CollaBiz ERP, DataVista, ChatVista & VistaBridge",
    description:
      "Software we built and still run: CollaBiz (B2B/ERP), DataVista (SAP-connected analytics), ChatVista (embeddable chatbot) and VistaBridge — an MCP server that connects Claude or ChatGPT to your own ERP and data.",
    path: "/products",
  },
  contact: {
    title: "Contact CafeVista | Scoped Plan Within 48 Hours",
    description:
      "Tell us what isn't working in the business and which systems you run. You get a scoped plan back within 48 hours. The first conversation costs nothing.",
    path: "/contactus",
  },
  terms: {
    title: "Terms & Conditions | CafeVista",
    description: "Terms and conditions for CafeVista services and this website.",
    path: "/terms",
  },
  notFound: {
    title: "Page Not Found | CafeVista",
    description: "The page you're looking for has moved or no longer exists.",
    path: "",
  },
};

/* Organization + site-wide structured data, injected once at app start. */
export const organizationJsonLd = (contact) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Cafevista Pvt Ltd",
  alternateName: ["CafeVista", "CafeVistaAI"],
  url: SITE_URL,
  logo: `${SITE_URL}/appLogo.png`,
  description:
    "Business solutions company: ERP integration, data platforms, business automation and custom software.",
  foundingDate: "2018",
  email: contact.email,
  telephone: contact.phone,
  address: {
    "@type": "PostalAddress",
    streetAddress: contact.address,
    addressLocality: "Islamabad",
    addressCountry: "PK",
  },
  sameAs: [
    contact.socials.linkedin,
    contact.socials.facebook,
    contact.socials.instagram,
    contact.socials.youtube,
  ].filter(Boolean),
});
